import { MAX_IMAGE_FILE_SIZE_LABEL } from '../utils/uploadConstants';

export const en = {
  pages: {
    home: "Home",
    bookmarks: "Bookmarks",
    myObjects: "Catalog",
    uploadObject: "Upload Object",
    activities: "Activities",
    settings: "Settings",
    curriculumVitae: "Curriculum Vitae"
  },
  navigation: {
    home: "Home",
    myObjects: "Catalog",
    catalogs: "Albums",
    uploadObject: "Upload Object",
    bookmarks: "Bookmarks",
    activities: "Activities",
    search: "Search...",
    searchTab: "Search",
    logoAlt: "Art Legacy Institute",
    adminPanel: "Admin Panel"
  },
  userProfile: {
    artLegacyId: "Art Legacy Institute ID:",
    notAssigned: "Not assigned",
    noEmail: "No email"
  },
  uploadQueue: {
    title: "Upload Queue",
    noUploads: "No uploads in queue",
    selectFiles: "Select files to start uploading",
    files: "files",
    uploading: "uploading",
    processing: "processing",
    completed: "completed",
    failed: "failed",
    pending: "pending",
    cancelled: "cancelled",
    cancelAll: "Cancel All",
    retryFailed: "Retry Failed",
    clearCompleted: "Clear Completed",
    expandQueue: "Expand queue",
    collapseQueue: "Collapse queue"
  },
  visibilityControl: {
    public: "Public",
    private: "Private"
  },
  collections: {
    title: "My Albums",
    createButton: "Create Album",
    createTitle: "Create New Album",
    nameLabel: "Album Name",
    namePlaceholder: "Enter album name",
    descriptionLabel: "Description",
    descriptionPlaceholder: "Enter album description",
    visibilityLabel: "Visibility",
    allItems: "Catalog",
    manageCatalogs: "Manage Albums",
    emptyTitle: "No albums yet",
    emptyDescription: "Create your first album to organize your artwork",
    itemCount: {
      singular: "item",
      plural: "items"
    },
    emptyCollection: "This album is empty",
    emptyCollectionDescription: "Add items to this album from the item detail page."
  },
  collectionSettings: {
    title: "Album Settings",
    tabs: {
      info: "Album Info",
      members: "Members"
    },
    info: {
      nameLabel: "Album Name",
      descriptionLabel: "Description",
      visibilityLabel: "Visibility",
      saveButton: "Save Changes",
      deleteButton: "Delete Album",
      dangerZone: "Danger Zone",
      deleteWarning: "Deleting an album is permanent and cannot be undone. All items will be removed from this album.",
      deleteTitle: "Delete Album",
      deleteMessage: "This action cannot be undone. All items will be removed from this album.",
      deleteConfirm: "Type the album name to confirm:",
      successUpdate: "Album updated successfully",
      successDelete: "Album deleted successfully"
    },
    members: {
      title: "Members",
      inviteButton: "Invite Member",
      emptyState: "No members yet",
      emptyDescription: "Invite someone to collaborate on this album.",
      columns: {
        name: "Name",
        email: "Email",
        role: "Role",
        status: "Status",
        actions: "Actions"
      },
      roles: {
        owner: "Owner",
        editor: "Editor",
        viewer: "Viewer"
      },
      status: {
        active: "Active",
        pending: "Pending",
        declined: "Declined"
      },
      removeButton: "Remove",
      removeTitle: "Remove Member",
      removeMessage: "Are you sure you want to remove this member?"
    },
    invite: {
      title: "Invite Member",
      emailLabel: "Email Address",
      roleLabel: "Role",
      inviteButton: "Send Invitation",
      success: "Invitation sent successfully"
    }
  },
  invitations: {
    title: "Catalog Invitations",
    emptyState: "No pending invitations",
    accept: "Accept",
    decline: "Decline",
    invitedBy: "Invited by",
    role: "Role"
  },
  catalogItems: {
    addToCatalog: "Add to Album",
    addToCatalogTitle: "Add to Album",
    selectCatalogs: "Select Albums",
    notesPlaceholder: "Add optional notes about this item in this album",
    addSuccess: "Item added to album successfully",
    removeSuccess: "Item removed from album successfully",
    addError: "Failed to add item to album",
    removeError: "Failed to remove item from album",
    inCatalogs: "In Albums:",
    noCatalogs: "Not in any catalogs",
    moreCatalogs: "+{count} more",
    selectCatalogToUpload: "Select which catalog to add these images to:"
  },
  catalogMembership: {
    title: "Albums",
    subtitle: "Add this artwork to one or more albums (optional)",
    label: "Select Albums",
    noCatalogs: "No catalogs available. Create a catalog first to organize your artworks.",
    waitingForSave: "Save artwork first to add to catalogs",
    saving: "Saving...",
    addError: "Failed to add to catalog",
    removeError: "Failed to remove from catalog"
  },
  roleDisplay: {
    admin: "Admin",
    catalogOwner: "Catalog Owner",
    legacyManager: "Legacy Manager",
    delegate: "Delegate",
    viewer: "Viewer",
    creator: "Creator",
    commercial: "General Catalog",
    id: "ID:"
  },
  roleProtectedRoute: {
    loading: "Loading..."
  },
  searchResults: {
    noResults: "No results found",
    pages: "Pages",
    artworks: "Artworks"
  },
  uploadProgress: {
    pending: "Pending",
    uploading: "Uploading",
    completed: "Upload Complete",
    processing: "Processing",
    ready: "Ready",
    failed: "Failed",
    cancelled: "Cancelled",
    unknown: "Unknown",
    remaining: "remaining",
    cancelUpload: "Cancel upload",
    retryUpload: "Retry upload",
    removeFromList: "Remove from list",
    thumbnailPreview: "Thumbnail preview",
    uploadProgressFor: "Upload progress for"
  },
  mfaSetup: {
    title: "Set up Two-Factor Authentication",
    settingUp: "Setting up your authenticator app...",
    loading: "Loading...",
    step1: "1. Open your authenticator app (Google Authenticator, Authy, etc.)",
    step2: "2. Scan this QR code:",
    step3: "3. Enter the 6-digit code from your app:",
    verificationCode: "Verification Code",
    cancel: "Cancel",
    verify: "Verify & Complete",
    verifying: "Verifying...",
    qrAlt: "QR Code for MFA setup"
  },
  mfaVerification: {
    title: "Enter Authentication Code",
    description: "Enter the 6-digit code from your authenticator app:",
    smsDescription: "Enter the 6-digit code sent to your phone:",
    authCode: "Authentication Code",
    cancel: "Cancel",
    verify: "Verify",
    verifying: "Verifying..."
  },
  publicPreview: {
    title: "Public Preview:",
    description: "This is how your content appears to public viewers. Only public attributes are shown.",
    closePreview: "Close Preview"
  },
  datePicker: {
    startDate: "Start Date",
    endDate: "End Date",
    date: "Date",
    preview: "Preview"
  },
  deviceTrust: {
    title: "Device Trust",
    description: "Trust this device to reduce MFA prompts. Trusted devices will skip MFA verification for future logins.",
    trusted: "Trusted",
    notTrusted: "Not Trusted",
    notRegistered: "Not Registered",
    unknown: "Unknown",
    thisTrusted: "This device is trusted",
    trustDevice: "Trust this device",
    refresh: "Refresh",
    securityNote: "Security Note",
    securityDescription: "Only trust devices you own and use regularly. Device trust requires MFA to be enabled on your account.",
    notEnabled: "Device tracking is not enabled for this session. Sign out and sign back in to enable device trust features.",
    trustSuccess: "Device marked as trusted. MFA will be skipped on future logins from this device.",
    forgetSuccess: "Device removed from trusted devices. You will need to complete MFA on future logins.",
    updateError: "Failed to update device trust status"
  },
  deviceTrustPrompt: {
    title: "Trust This Device?",
    description: "Skip MFA on future logins from this device. Only enable on devices you own.",
    trustLabel: "Trust this device and skip MFA for future logins",
    continue: "Continue",
    settingUp: "Setting up...",
    trustFailed: "Device trust failed, but you can continue. Manage this in Settings later."
  },
  dynamicFields: {
    dragToReorder: "Drag to reorder"
  },
  editionCounter: {
    count: "Count"
  },
  artworkGrid: {
    loading: "Loading...",
    imageError: "Failed to load image",
    createdOn: "Created on"
  },
  activityTimeline: {
    days: "Days",
    activityType: "Activity Type",
    allActivities: "All Activities",
    noActivities: "No activities found",
    resource: "Resource:",
    email: "Email:",
    details: "Details:",
    editor: "Editor",
    catalogFilter: "Catalog",
    catalogFilterPlaceholder: "e.g. DA-0PK.0003",
    errorLoading: "Failed to load activities",
    filtering: "Filtering...",
    activities: {
      catalogCreate: "Created Catalog",
      catalogUpdate: "Updated Catalog",
      catalogDelete: "Deleted Catalog",
      catalogVisibilityChange: "Changed Visibility",
      userDataExport: "Data Export"
    },
    timeRanges: {
      oneDay: "1 Day",
      sevenDays: "7 Days",
      thirtyDays: "30 Days"
    }
  },
  advancedFilter: {
    filter: "Filter",
    title: "Advanced Filter",
    basicInfo: "Basic Information",
    artworkTitle: "Artwork Title",
    artworkTitlePlaceholder: "Search by artwork title",
    keywords: "Keywords",
    keywordsPlaceholder: "Enter keywords separated by commas",
    provenance: "Provenance",
    provenancePlaceholder: "Search by ownership history",
    artworkDates: "Creation Year",
    dateFrom: "Year From",
    dateTo: "Year To",
    dateFromPlaceholder: "1900",
    dateToPlaceholder: "2026",
    dimensions: "Dimensions",
    unit: "Unit",
    inches: "in.",
    centimeters: "cm",
    height: "Height:",
    width: "Width:",
    to: "to",
    classification: "Classification",
    workType: "Getty Work Type",
    workTypePlaceholder: "e.g. painting, sculpture",
    materials: "Getty Materials",
    materialsPlaceholder: "e.g. oil paint, canvas",
    techniques: "Getty Techniques",
    techniquesPlaceholder: "e.g. brushwork, etching",
    creator: "Creator",
    creatorPlaceholder: "Search by artist name",
    clearAll: "Clear All",
    cancel: "Cancel",
    applyFilters: "Apply Filters",
    rangeErrorHeightFromTo: "Height: \"From\" must not exceed \"To\"",
    rangeErrorWidthFromTo: "Width: \"From\" must not exceed \"To\"",
    rangeErrorYearFromTo: "Year: \"From\" must not exceed \"To\""
  },
  imageThumbnail: {
    altText: "Selected artwork",
    addingInfo: "Adding information for this artwork",
    addingInfoWithTitle: "Adding information for \"{title}\"",
    catalogNumberPrefix: "Catalog:"
  },
  signup: {
    title: "Create Your Account",
    accountSetup: "Account Setup",
    steps: {
      accountCreation: "Account Creation",
      termsAndConditions: "Terms and Conditions",
      paymentProcessing: "Payment Processing",
      legacyPlanning: "Legacy Planning (Optional)"
    },
    accountCreation: {
      title: "Account Creation",
      viewerDescription: "Email and password registration. Email verification required. Basic profile information (name only).",
      creatorDescription: "Email and password registration. Email verification required. Basic profile information (name only).",
      generalCatalogDescription: "Email and password registration. Email verification required. Basic profile information (name, organization)."
    },
    termsAndConditions: {
      title: "Terms and Conditions",
      description: "Please review and accept our Terms and Conditions to complete your registration.",
      agreeLabel: "I agree to the Terms and Conditions"
    },
    paymentProcessing: {
      title: "Payment Processing",
      description: "Stripe integration for {tier} tier subscription.",
      paymentInfo: "Payment integration will be implemented with Stripe.",
      selectedPlan: "Selected Plan:",
      subscriptionIncludes: "Your subscription includes:",
      creatorFeatures: {
        singleCatalog: "Single catalog",
        delegates: "Up to 3 delegates",
        legacyManagers: "Up to 3 legacy managers",
        assignedId: "Assigned ID"
      },
      generalCatalogFeatures: {
        multipleCatalogs: "Multiple catalogs",
        catalogItemLimit: "Up to 650 catalog items",
        delegates: "Up to 10 delegates",
        legacyManagers: "Up to 3 legacy managers",
        assignedId: "Assigned ID"
      }
    },
    legacyPlanning: {
      title: "Legacy Planning (Optional)",
      description: "Designate legacy managers. Set succession instructions. Configure posthumous content handling.",
      skipInfo: "You can skip this step and configure legacy planning later in your account settings.",
      legacyManagerEmail: "Legacy Manager Email",
      legacyManagerEmailHelper: "Optional: Email of person to manage your catalog after death",
      successionInstructions: "Succession Instructions",
      successionInstructionsHelper: "Optional: Instructions for your legacy manager"
    },
    form: {
      email: "Email",
      password: "Password", // pragma: allowlist secret
      confirmPassword: "Confirm Password", // pragma: allowlist secret
      firstName: "First Name",
      lastName: "Last Name",
      displayName: "Display Name",
      displayNameHelper: "Your public display name for artwork attribution (e.g., your artist name)",
      organization: "Organization"
    },
    validation: {
      requiredFields: "Please fill in all required fields",
      displayNameRequired: "Display name is required",
      displayNameMaxLength: "Display name must be 100 characters or less",
      displayNameReserved: "Please enter your real display name",
      passwordMismatch: "Passwords do not match", // pragma: allowlist secret
      organizationRequired: "Organization is required for General Catalog plan",
      termsRequired: "You must agree to the Terms and Conditions",
      signupFailed: "Signup failed. Please try again."
    },
    actions: {
      cancel: "Cancel",
      back: "Back",
      next: "Next",
      completeSignup: "Complete Signup",
      processing: "Processing..."
    },
    startOver: {
      link: "Having trouble? Start over",
      dialogTitle: "Start Over?",
      dialogMessage: "This will clear your current signup progress and let you start fresh. Any information you've entered will be lost.",
      confirm: "Start Over",
      cancel: "Continue Signup"
    },
    existingEmail: {
      infoMessage: "We've sent instructions to your email address. If you already have an account, please",
      signInLink: "sign in instead",
    },
    consent: {
      required: "You must agree to the Terms of Service and Privacy Policy to create an account",
      label: "I agree to the",
      termsLink: "Terms of Service",
      and: "and",
      privacyLink: "Privacy Policy",
    },
    planSelection: {
      helpMeChoose: "Viewer is free for browsing. Creator lets individual artists catalog and protect their work. General Catalog is for galleries or institutions managing multiple artists work.",
      helpMeChooseNoCommercial: "Viewer is free for browsing. Creator lets individual artists catalog and protect their work.",
      expandedDescriptions: {
        viewer: "Best for art enthusiasts who want to browse, search, and bookmark public collections. No catalog creation.",
        creator: "Best for individual artists and collectors who want to catalog their work, assign delegates, and set up legacy planning.",
        commercial: "Best for galleries, estates, and institutions that manage multiple catalogs across teams with expanded delegate access.",  // key stays 'commercial' (internal tier ID)
      },
      tooltips: {
        aliId: "Your unique Art Legacy Institute identifier (e.g., JD-3K7). Used to label every object in your catalog.",
        catalog: "A digital collection of your artwork records, including images, provenance, and metadata.",
        delegate: "A person you authorize to view or edit your catalog on your behalf.",
        legacyManager: "A person designated to manage your catalog after your passing, ensuring long-term preservation.",
        multipleCatalogs: "Manage separate catalogs for different collections, artists, or departments under one account.",
      },
    },
  },
  profile: {
    userName: "User",
    userEmail: "No email",
    curriculumVitae: "Curriculum Vitae",
    settings: "Settings",
    logout: "Logout",
    download: "Download",
    edit: "Edit",
    share: "Share",
    socialMedia: "Social Media",
    education: "Education",
    soloExhibitions: "Solo Exhibitions",
    groupExhibitions: "Group Exhibitions",
    publications: "Publications",
    awardsHonors: "Awards & Honors",
    collections: "Collections",
    contactInformation: "Contact Information",
    noBiographyAvailable: "No biography available",
    noSocialMediaAvailable: "No social media links available",
    noEducationAvailable: "No education information available",
    noSoloExhibitionsAvailable: "No solo exhibitions available",
    noGroupExhibitionsAvailable: "No group exhibitions available",
    noPublicationsAvailable: "No publications available",
    noAwardsAvailable: "No awards or honors available",
    noCollectionsAvailable: "No collections available",
    noContactAvailable: "No contact information available",
    editCurriculumVitae: "Edit Curriculum Vitae",
    clearAll: "Clear All",
    saveChanges: "Save Changes",
    saveChangesTitle: "Save Changes",
    saveChangesMessage: "Are you sure you want to save changes?",
    yes: "Yes",
    no: "No",
    profilePicture: "Profile Picture",
    biography: "Biography",
    biographySubtitle:
      "Provide a brief overview of your career, artistic background, and any personal or professional statement you want to share. Optional.",
    socialMediaSubtitle:
      "Add links to your social media below to share. Optional.",
    educationSubtitle:
      "List academic degrees, certificates, or other relevant training. You can add multiple entries.",
    soloExhibitionsSubtitle:
      "List exhibitions where you were the sole or primary artist. Include dates, venues, and locations. Optional.",
    groupExhibitionsSubtitle:
      "List exhibitions with multiple artists, including dates, venues, and locations. Optional.",
    publicationsSubtitle:
      "Record articles, catalogs, books, or media mentions that feature your work. Optional.",
    awardsHonorsSubtitle:
      "List any awards, prizes, grants, or recognitions you have received. Optional.",
    collectionsSubtitle:
      "List public institutions (museums, foundations) or private collections (individuals or corporations) where your work is held. Both are visible to the public. Optional.",
    galleryRepresentation: "Gallery Representation",
    noGalleryRepresentationAvailable: "No gallery representation available",
    galleryRepresentationSubtitle:
      "List galleries that represent you and regularly show or sell your work. Optional.",
    contactInformationSubtitle:
      "Add your email and phone number to allow others to contact you. Optional.",
    uploadPhoto: "Upload Photo",
    addSocialMedia: "Add Social Media",
    addEducation: "Add Education",
    addSoloExhibition: "Add Solo Exhibition",
    addGroupExhibition: "Add Group Exhibition",
    addPublication: "Add Publication",
    addAward: "Add Award",
    addCollection: "Add Collection",
    addContact: "Add Contact",
    socialMediaType: "Social Media Type",
    link: "Link",
    yearsAttended: "Years Attended",
    institutionSchool: "Institution/School",
    degreeCertificate: "Degree/Certificate",
    yearOfExhibition: "Year of Exhibition",
    exhibition: "Exhibition",
    yearPublished: "Year Published",
    publication: "Publication",
    yearAwarded: "Year Awarded",
    awardHonor: "Award / Honor",
    organization: "Organization",
    collection: "Collection",
    contactType: "Contact Type",
    contactValue: "Contact Value",
    commercial: {
      about: "About",
      editAbout: "Edit About",
      aboutUs: "About Us",
      aboutUsSubtitle: "Tell visitors about your gallery, dealership, or collection.",
    },
  },
  app: {
    title: "Art Legacy Institute",
    addNewObject: "Add New Object",
    viewAsPublic: "View as Public",
    bulkUpload: "Bulk Upload",
  },
  footer: {
    termsAndPrivacy: "Terms and Conditions & Privacy Policy",
    termsTab: "Terms and Conditions",
    privacyTab: "Privacy Policy",
    close: "Close",
    copyright: "© {year} Art Legacy Institute (ALI). All rights reserved.",
    copyrightExtended: "Artwork and images are owned and copyrighted by their respective creator and are displayed with permission.",
    betaFeedback: "Beta Feedback"
  },
  home: {
    title: "Art Legacy Institute",
    searchDescription: "Search and discover artwork and users by title, catalog ID, ALI ID, username, or full name.",
    searchPlaceholder: "Search by title, catalog ID, ALI ID, username, or name...",
    searchPlaceholderArtworks: "Search artworks...",
    myObjects: "Catalog",
    noArtworkYet: "No artwork uploaded yet",
    noArtworkDescription: "Start building your digital catalog by uploading your first artwork.",
    uploadFirstArtwork: "Upload Your First Artwork",
    pleaseSignIn: "Please sign in to view your artwork",
    signInDescription: "Sign in to access your digital catalog and manage your artwork collection.",
    signIn: "Sign In",
    checkingAuth: "Checking authentication...",
    loadingArtwork: "Loading your artwork...",
    failedToLoad: "Failed to load artwork:"
  },
  settings: {
    title: "Settings",
    tabs: {
      account: "Account",
      help: "Help",
      display: "Display",
      privacy: "Privacy",
    },
    account: {
      passwordReset: {
        title: "Password Reset",
        current: "Current Password",
        new: "New Password",
        confirm: "Confirm Password",
        button: "Update Password",
      },
      profile: {
        title: "Account Profile",
        fullName: "Full Name",
        instituteId: "Institute ID",
        email: "Email",
      },
      identifiers: {
        title: "Creator Identifiers",
        platform: "Platform",
        id: "ID",
        add: "Add",
        platforms: {
          getty: "Getty",
          orcid: "ORCID",
          isni: "ISNI",
          other: "Other",
        },
      },
      marketingConsent: {
        title: "Marketing Data Sharing",
        description: "Allow Art Legacy Institute to include your contact information in CRM exports for marketing communications and partnership opportunities.",
        enabled: "Marketing consent enabled",
        disabled: "Marketing consent disabled",
        updateError: "Failed to update marketing consent",
      },
    },
    help: {
      documentation: {
        title: "Documentation",
        description: "Access user guides and documentation",
      },
      support: {
        title: "Support",
        description: "Contact support for assistance",
      },
      faq: {
        title: "FAQ",
        description: "Frequently asked questions",
      },
    },
    delegates: {
      title: "Delegates",
      description: "Grant temporary access to specific private catalogs. Delegates can view, search, and collaborate on assigned content with time-limited permissions.",
      invite: {
        title: "Invite a Delegate",
        email: "Email Address",
        emailHelper: "Enter the email of the person you want to invite",
        delegateName: "Delegate Name",
        delegateNameHelper: "Enter the delegate's name (optional)",
        collection: "Album",
        collectionHelper: "Select which album to share",
        permission: "Permission Level",
        permissionHelper: "Choose what the delegate can do",
        canEditCv: "Allow editing my CV",
        canEditCvHelper: "Let this delegate edit your CV (independent of catalog access)",
        expiry: "Invitation Expiry (days)",
        expiryHelper: "Number of days before the invitation expires (1-90)",
        message: "Message (optional)",
        messageHelper: "Add a personal message to the invitation",
        send: "Send Invitation",
        sending: "Sending...",
        success: "Invitation sent successfully!",
        errorEmail: "Please enter a valid email address",
        errorCollection: "Please select an album",
        errorExpiry: "Expiry must be between 1 and 90 days",
      },
      pending: {
        title: "Pending Invitations",
      },
      active: {
        title: "Active Delegations",
      },
      edit: {
        title: "Edit Delegation",
        collectionHelper: "Change which album (or the whole catalog) this delegate can access",
      },
      empty: "No pending invitations or active delegations.",
      permissions: {
        view: "View",
        full: "Full Access",
      },
      cvEditChip: "CV edit",
      revoke: {
        invitationTitle: "Revoke Invitation",
        invitationDescription: "Are you sure you want to revoke this invitation? The recipient will no longer be able to accept it.",
        delegationTitle: "Revoke Delegation",
        delegationDescription: "Are you sure you want to revoke access for this delegate? They will immediately lose access to the catalog.",
      },
    },
  },
  wizard: {
    title: "Upload Object",
    privacyNotice:
      "Your changes are automatically saved. All information is kept private by default. You can adjust privacy settings in the wizard.",
    privacyNoticeShort: "Autosaved & private by default. Adjust privacy per field in the wizard.",
    backToMyObjects: "Back to Catalog",
    uploadObjectTitle: "Upload Object",
    stepPrefix: "Step",
    cancel: "Cancel",
    previous: "Previous",
    next: "Next",
    finished: "Finished",
    stepLabels: {
      upload: ["Add Your Images", "Choose Your Main Image"],
      describe: [
        "Add Creators",
        "Object Title & Location",
        "Signature",
        "Creation Date",
      ],
      media: [
        "Getty Work Type",
        "Getty Techniques",
        "Getty Materials",
        "Getty Substrate",
      ],
      measure: ["Measurements"],
      catalogKeywords: ["Inventory Number", "Keywords"],
      provenance: ["Provenance", "Exhibitions", "Publications", "Related Works"],
      additionalInfo: ["Additional Notes", "Always Private"]
    },
    tabs: {
      upload: "Upload",
      describe: "Describe",
      media: "Media",
      measure: "Measure",
      catalogKeywords: "Inventory and Albums",
      provenanceReferences: "Provenance and References",
      additionalInfo: "Additional Info",
      review: "Review & Finish"
    },
    stepTitles: {
      upload: "Upload Images",
      describe: "Describe Artwork",
      media: "Media",
      measure: "Measurements",
      catalogKeywords: "Inventory & Albums",
      provenance: "Provenance & References",
      additionalInfo: "Additional Information",
      review: "Review & Finish"
    },
    autosave: {
      saving: "Saving...",
      saved: "All changes saved",
      error: "Error saving",
      retry: "Retry",
      lastSaved: "Last saved"
    },
    additionalNotes: {
      title: "Additional Notes",
      subtitle: "Add any extra details, context, or special information about this step. Optional.",
    },
    steps: {
      step1: "Upload Files",
      step2: "Add Details",
      step3: "Set Measurements",
      step4: "Document History",
      step5: "Final Review",
    },
    upload: {
      addImages: {
        title: "Add Your Images",
        subtitle:
          "Upload photos of your artwork or take new ones with your camera",
        dropText: "Drag and drop or browse files to upload",
        dropHere: "Drop files here",
        camera: "Camera",
        files: "Files",
        selected: "selected",
        image: "image",
        images: "images",
        supportText: "or click to select files",
        supportedFormats: "Supported: JPEG, PNG, HEIC, WebP, GIF, TIFF, RAW • Max {maxSize}MB per file"
      },
      selectCover: {
        title: "Choose Your Main Image",
        subtitle: "Select the image that best represents your artwork",
        noImages: "Please upload images first to select a cover image.",
        selected: "✓ Main image selected",
      },
    },
    describe: {
      additionalNotes: {
        title: "Additional Notes",
        subtitle:
          "Add any extra details, context, or special information about this step. Optional.",
        placeholder: "Enter additional notes...",
        label: "Note",
        addButton: "Add Note",
      },
      steps: {
        creators: {
          title: "Creators",
          subtitle: "Add any co-creators who contributed to this object.",
          ownerLabel: "Creator",
          ownerHelperText: "Primary creator for this catalog entry",
          coCreatorLabel: "Co-Creator",
          displayName: "Display Name", // Keep for backwards compatibility
          addButton: "Add co-creator",
          defaultUser: "Current User (cannot be removed)", // DEPRECATED but keep
          commercial: {
            title: "Artists",
            subtitle: "Add the artist(s) who created this artwork.",
            artistLabel: "Artist",
            addButton: "Add artist",
          },
        },
        titleLocation: {
          objectTitle: {
            title: "Artwork Title",
            subtitle:
              'Enter title of this object. If no title, enter "Untitled".',
            label: "Title",
          },
          locationOfTitle: {
            title: "Title Location",
            subtitle:
              'Where does the title appear on the artwork? (e.g., "bottom right", "verso")',
            label: "Title Location",
          },
          alternativeTitles: {
            title: "Alternative Titles",
            subtitle: "List any other names this artwork is known by.",
            label: "Alternative Title",
            addButton: "Add Title",
          },
          titleDisplay: {
            title: "Title as Written",
            subtitle:
              "How exactly does the title appear on the object. Include any special formatting or styling.",
            label: "Title Display",
          },
        },
        signature: {
          signatureType: {
            title: "Signature Information",
            subtitle: "Record how the object is signed, where the signature appears, and what it looks like",
            label: "Signature Type",
            options: [
              "Signed",
              "Monogrammed",
              "Initialed",
              "Stamped",
              "Inscribed",
              "Artist's mark",
              "Foundry mark",
              "Publisher's mark",
              "Embossed",
            ],
          },
          locationOfSignature: {
            title: "Location of Signature",
            subtitle: "(e.g., lower left, l/l, verso, overlap, in plate, etc)",
            label: "Signature Location",
          },
          signatureDisplay: {
            title: "Signature Display",
            subtitle:
              "How exactly does the signature appear on the object. Include any special formatting or styling.",
            label: "Signature Display",
          },
          addButton: "Add Additional Signature",
        },
        creationDate: {
          creationDate: {
            title: "Creation Date",
            subtitle:
              "Enter when your object was created. You can provide an exact date, approximate date (circa), or a date range.",
            label: "Date Type",
            options: [
              "Month/Day/Year",
              "Month/Year",
              "Year",
              "Circa",
              "Date Range",
              "Date Range (Circa)",
            ],
          },
          locationOfDate: {
            title: "Location of Date",
            subtitle: "(e.g., lower left, l/l, verso, overlap, in plate, etc)",
            label: "Location of Date",
          },
          dateDisplay: {
            title: "Date Display",
            subtitle:
              "How exactly does the date appear on the object. Include any special formatting or styling.",
            label: "Date Display",
          },
        },
        workType: {
          workType: {
            title: "Getty Work Type",
            subtitle:
              "Select the type that best describes your object, (e.g. painting, sculpture, photograph).",
            label: "Getty Work Type",
            multiplesCheckbox:
              "If multiples exist of object, select this checkbox",
            options: [
              "Painting",
              "Sculpture",
              "Photograph",
              "Drawing",
              "Print",
              "Textile",
              "Ceramic",
              "Mixed Media",
              "Installation",
              "Video",
              "Digital Art",
              "Collage",
              "Watercolor",
              "Oil Painting",
              "Acrylic Painting",
            ],
          },
          editions: {
            title: "Editions",
            subtitle: "Create an edition type for each of your editions as needed.",
            helperText: "Select the number of objects appearing in each of your edition(s) below.",
            label: "Select Edition Type",
            addButton: "Add",
            options: [
              "Main Edition",
              "Artist Proof",
              "Printer's Proof",
              "Trial Proof",
              "Bon à Tirer",
              "Hors Commerce",
              "Publisher's Proof",
              "Working Proof",
              "Other (Free Text)",
            ],
            freeTextSentinel: "Other (Free Text)",
          },
        },
        techniques: {
          title: "Getty Techniques",
          subtitle:
            "Select one or more techniques or methods used in creating your object (e.g., etching, oil painting). If your technique is not listed, you can enter it manually.",
          label: "Getty Techniques",
          options: [
            "Oil painting",
            "Acrylic painting",
            "Watercolor",
            "Etching",
            "Lithography",
            "Screen printing",
            "Woodcut",
            "Engraving",
            "Drypoint",
            "Mezzotint",
            "Aquatint",
            "Collage",
            "Mixed media",
            "Digital printing",
            "Photography",
            "Sculpture",
            "Ceramics",
            "Weaving",
            "Embroidery",
            "Casting",
          ],
        },
        materials: {
          title: "Getty Materials",
          subtitle:
            "Select one or more physical substances or ingredients used in creating your artwork (e.g., bronze, oil paint, graphite). If your material is not listed, you can enter it manually.",
          label: "Getty Materials",
          options: [
            "Oil paint",
            "Acrylic paint",
            "Watercolor",
            "Graphite",
            "Charcoal",
            "Ink",
            "Bronze",
            "Marble",
            "Clay",
            "Wood",
            "Canvas",
            "Paper",
            "Linen",
            "Silk",
            "Cotton",
            "Gold leaf",
            "Silver",
            "Copper",
            "Steel",
            "Glass",
            "Plastic",
            "Resin",
            "Fabric",
            "Thread",
            "Wire",
          ],
        },
        substrate: {
          title: "Getty Substrate",
          subtitle:
            "Select the underlying surfaces or supports on which your artwork is applied (e.g., canvas, wood, paper). If your substrate is not listed, you can enter it manually.",
          label: "Getty Substrate",
          options: [
            "Canvas",
            "Wood",
            "Paper",
            "Linen",
            "Cotton",
            "Silk",
            "Metal",
            "Glass",
            "Plastic",
            "Stone",
            "Ceramic",
            "Board",
            "Cardboard",
            "Fabric",
            "Leather",
            "Plaster",
            "Wall",
            "Panel",
          ],
        },
        mediaAdditionalComments: {
          title: "Additional Comments",
          subtitle:
            "Add any additional notes, such as conservation issues, brands of materials, etc. Optional — doesn't affect section completion.",
          placeholder:
            "e.g., Conservation treatment done 2015, Liquitex Heavy Body Acrylics, canvas primed with rabbit-skin glue",
        },
      },
      measure: {
        measurements: {
          title: "Measurements",
          subtitle:
            "Record the dimensions of your object. Select your preferred unit of measurement, then enter only the fields that apply (e.g., width, height, depth). Fractions are supported for inches.",
          substrateCheckbox:
            "If your substrate size differs from your object size (e.g. paper size is larger than the print area), select this checkbox to note it here",
        },
        substrateMeasurements: {
          title: "Substrate Measurements",
          subtitle:
            "Record the dimensions of your substrate. Select your preferred unit of measurement, then enter only the fields that apply (e.g., width, height, depth). Fractions are supported for inches.",
        },
        units: {
          inches: "Inches",
          mm: "mm",
          cm: "cm",
          feet: "Feet",
          meters: "Meters",
        },
        weightUnits: {
          lbs: "Pounds",
          oz: "Ounces",
          kg: "Kilograms",
          g: "Grams",
        },
        volumeUnits: {
          L: "Liters",
          mL: "Milliliters",
          gal: "Gallons",
          fl_oz: "Fluid Ounces",
          cc: "Cubic Centimeters",
        },
        dimensions: {
          height: "Height",
          width: "Width",
          depth: "Depth",
          diameter: "Diameter",
          length: "Length",
          thickness: "Thickness",
          radius: "Radius",
          circumference: "Circumference",
          weight: "Weight",
          mass: "Mass",
          volume: "Volume",
        },
        addButton: "Add",
      },
      placeholder: {
        title: "Step",
        subtitle: "This step is not yet implemented",
        label: "Placeholder content",
      },
    },
    measure: {
      measurements: {
        title: "Measurements",
        subtitle:
          "Record the dimensions of your object. Select your preferred unit of measurement, then enter only the fields that apply (e.g., width, height, depth). Fractions are supported for inches.",
        substrateCheckbox:
          "If your substrate size differs from your object size (e.g. paper size is larger than the print area), select this checkbox to note it here",
      },
      substrateMeasurements: {
        title: "Substrate Measurements",
        subtitle:
          "Record the dimensions of your substrate. Select your preferred unit of measurement, then enter only the fields that apply (e.g., width, height, depth). Fractions are supported for inches.",
      },
      units: {
        inches: "Inches",
        mm: "mm",
        cm: "cm",
        feet: "Feet",
        meters: "Meters",
      },
      weightUnits: {
        lbs: "Pounds",
        oz: "Ounces",
        kg: "Kilograms",
        g: "Grams",
      },
      volumeUnits: {
        L: "Liters",
        mL: "Milliliters",
        gal: "Gallons",
        fl_oz: "Fluid Ounces",
        cc: "Cubic Centimeters",
      },
      dimensions: {
        height: "Height",
        width: "Width",
        depth: "Depth",
        diameter: "Diameter",
        length: "Length",
        thickness: "Thickness",
        radius: "Radius",
        circumference: "Circumference",
        weight: "Weight",
        mass: "Mass",
        volume: "Volume",
      },
      addButton: "Add",
    },
    provenance: {
      provenance: {
        title: "Provenance",
        subtitle:
          "Record the ownership history of the object. The Provenance can be public or private depending on the permissions. The Private Only Provenance is always private.\n\nAn example of an entry is\nProvenance: Sold to private collector\nPrivate: Sold to David for $25k",
        internalLabel: "Always Private",
        publicLabel: "Provenance",
        addButton: "Add Provenance",
      },
      exhibitions: {
        title: "Exhibitions",
        subtitle:
          "List exhibitions where this object has been displayed, including dates, venues, and any notable details. Optional.",
        exhibitionLabel: "Exhibition",
        addButton: "Add Exhibition",
        addToCV: "Add to CV",
        addToCVSuccess: "Exhibition added to your CV",
        placeholder: "Type or select from your CV",
        exhibitionOptions: [
          "Solo Exhibition",
          "Group Exhibition",
          "Museum Exhibition",
          "Gallery Exhibition",
          "Art Fair",
          "Biennial",
          "Triennial",
          "Festival",
          "Private Exhibition",
          "Online Exhibition",
        ],
      },
      publications: {
        title: "Publications",
        subtitle:
          "Provide references to publications, catalogs, or articles where this object has been featured. Include titles, authors, and publication dates if available. Optional.",
        label: "Publications",
        addButton: "Add Publication",
        addToCV: "Add to CV",
        addToCVSuccess: "Publication added to your CV",
        placeholder: "Type or select from your CV",
      },
      awards: {
        title: "Awards & Honors",
        subtitle:
          "Reference awards, prizes, grants, or honors associated with this object. Pick from awards already on your CV, or type a new one. Optional.",
        label: "Award",
        addButton: "Add Award",
        addToCV: "Add to CV",
        addToCVSuccess: "Award added to your CV",
        placeholder: "Type or select from your CV",
      },
      relatedWorks: {
        title: "Related Works",
        subtitle:
          "Provide references to objects uploaded that are related to this object",
        label: "Related Work",
        addButton: "Add Related Work",
        placeholder: "Search by title or catalog number...",
        noMatchesText: "No matching works found",
      },
    },
    additionalInfo: {
      catalogNumber: {
        title: "Inventory Number",
        subtitle:
          "Enter your inventory number for this object. The first inventory number is sortable. Additional inventory numbers can also be added. Optional.",
        aliLabel: "ALI Catalog Number",
        aliValue: "Not assigned",
        catalogLabel: "Inventory Number",
        addButton: "Add Inventory Number",
      },
      keywords: {
        title: "Keywords",
        subtitle: "Optional keywords for later search filters",
        label: "Add Keyword",
        addButton: "Add",
        helperText: "Separate multiple keywords with commas",
      },
      relatedWorks: {
        title: "Related Works",
        subtitle:
          "Provide references to objects uploaded that are related to this object",
        label: "Related Work",
        addButton: "Add Related Work",
        placeholder: "Search by title or catalog number...",
        noMatchesText: "No matching works found",
      },
      additionalNotesPublic: {
        title: "Additional Notes - Public",
        subtitle:
          "Include any final notes or context about your object that were not captured in earlier steps. Optional.",
        label: "Public Note",
        addButton: "Add Public Note",
      },
      additionalNotesPrivate: {
        title: "Additional Notes - Private",
        subtitle:
          "Include any final notes or context about your object that were not captured in earlier steps. Optional.",
        label: "Private Note",
        addButton: "Add Private Note",
      },
      internalOnlyData: {
        title: "Always Private",
        subtitle:
          "Record private notes, the artwork's current physical location, and estimated value. This information is for internal records and will not be displayed publicly.",
        locationLabel: "Physical Location",
        valueLabel: "Estimated Value",
        privateNotesLabel: "Private Note",
        privateNotesAddButton: "Add Private Note",
      },
    },
  },
  upload: {
    // Core upload status and progress
    status: {
      pending: 'Pending',
      uploading: 'Uploading',
      completed: 'Completed',
      failed: 'Failed',
      cancelled: 'Cancelled'
    },
    progress: {
      uploading: 'Uploading...',
      completed: 'Upload complete',
      failed: 'Upload failed',
      remaining: 'remaining',
      speed: 'Upload speed',
      uploadingFile: 'Uploading {fileName}',
      percentComplete: '{percent}% complete',
      uploadSpeed: '{speed}',
      timeRemaining: '{time}'
    },

    // Queue management
    queue: {
      title: 'Upload Queue',
      empty: 'No uploads in queue',
      cancelAll: 'Cancel All',
      retryFailed: 'Retry Failed',
      clearCompleted: 'Clear Completed'
    },
    summary: {
      totalFiles: 'Total files',
      uploading: 'Uploading',
      completed: 'Completed',
      failed: 'Failed',
      pending: 'Pending',
      uploadingCount: '{count} uploading',
      completedCount: '{count} completed',
      failedCount: '{count} failed'
    },

    // User interactions
    actions: {
      cancel: 'Cancel',
      retry: 'Retry',
      remove: 'Remove',
      upload: 'Upload'
    },
    confirmations: {
      cancelUpload: 'Are you sure you want to cancel this upload?',
      removeFile: 'Are you sure you want to remove this file?',
      cancelAll: 'Are you sure you want to cancel all uploads?'
    },

    // File handling and validation
    validation: {
      supportedFormats: 'Supported formats: JPEG, PNG, HEIC, WebP, GIF, TIFF, RAW',
      maxFileSize: `Maximum file size: ${MAX_IMAGE_FILE_SIZE_LABEL}`,
      selectFiles: 'Select files to upload',
      fileSizeExceeded: 'File size exceeds maximum limit of {maxSize}'
    },
    dragDrop: {
      dropHere: 'Drop files here to upload',
      dragOver: 'Drop files here',
      clickToSelect: 'or click to select files'
    },

    // Error handling
    errors: {
      invalidFormat: 'Invalid file format. Please select a supported image format.',
      fileTooLarge: `File is too large. Maximum file size is ${MAX_IMAGE_FILE_SIZE_LABEL}.`,
      uploadFailed: 'Upload failed. Please try again.',
      networkError: 'Network error occurred. Please check your connection.',
      serverError: 'Server error occurred. Please try again later.',
      chunkUploadFailed: 'Failed to upload file chunk. Retrying...',
      initiationFailed: 'Failed to initiate upload. Please try again.',
      completionFailed: 'Failed to complete upload. Please try again.',
      authenticationFailed: 'Authentication failed. Please log in again.'
    },

    // Formatting and units
    fileCount: {
      singular: 'file',
      plural: 'files'
    },
    units: {
      bytes: 'B',
      kb: 'KB',
      mb: 'MB',
      gb: 'GB',
      bytesPerSecond: 'B/s',
      kbPerSecond: 'KB/s',
      mbPerSecond: 'MB/s'
    },
    time: {
      seconds: 'seconds',
      minutes: 'minutes',
      hours: 'hours',
      remaining: 'remaining',
      secondsRemaining: '{seconds}s remaining',
      minutesRemaining: '{minutes}m {seconds}s remaining',
      hoursRemaining: '{hours}h {minutes}m remaining'
    },

    // Accessibility and user guidance
    aria: {
      uploadProgress: 'Upload progress for {fileName}',
      cancelUpload: 'Cancel upload',
      retryUpload: 'Retry upload',
      removeFile: 'Remove file from list',
      uploadQueue: 'Upload queue with {count} files',
      uploadStarted: 'Upload started for {fileName}',
      uploadCompleted: 'Upload completed for {fileName}',
      uploadFailed: 'Upload failed for {fileName}',
      uploadCancelled: 'Upload cancelled for {fileName}'
    },
    instructions: {
      selectFiles: 'Select files to upload',
      dragAndDrop: 'Drag and drop files here or click to select',
      supportedFormats: 'Supported formats: JPEG, PNG, HEIC, WebP, GIF, TIFF, RAW',
      maxFileSize: `Maximum file size: ${MAX_IMAGE_FILE_SIZE_LABEL} per file`
    },
    help: {
      multipleFiles: 'You can select multiple files at once',
      uploadProcess: 'Files are uploaded securely to your private storage',
      troubleshooting: 'If uploads fail, check your internet connection and try again'
    }
  },
  activities: {
    description: "View your recent account activities and actions."
  },
  changePassword: {
    title: "Change Password",
    subtitle: "You must change your password before continuing",
    newPassword: "New Password", // pragma: allowlist secret
    confirmPassword: "Confirm Password", // pragma: allowlist secret
    passwordHelp: "Password must be at least 8 characters", // pragma: allowlist secret
    changeButton: "Change Password",
    changing: "Changing Password...",
    errors: {
      passwordMismatch: "Passwords do not match", // pragma: allowlist secret
      passwordTooShort: "Password must be at least 8 characters long", // pragma: allowlist secret
      changeFailed: "Password change failed"
    }
  },
  catalogSwitcher: {
    myCatalog: "My Catalog",
    catalog: "Catalog",
    ownerChip: "Owner",
    delegatedChip: "Delegate",
    permission: {
      view: "View",
      full: "Full"
    },
    delegatedBanner: "Viewing {aliId}'s catalog with {permission} access",
    backToMyCatalog: "Back to My Catalog"
  },
  forgotPassword: {
    title: "Reset Password",
    steps: {
      enterUsername: "Enter Username",
      enterCodeAndPassword: "Enter Code & New Password" // pragma: allowlist secret
    },
    enterUsernameDescription: "Enter your username to receive a password reset code",
    enterCodeDescription: "Enter the verification code sent to your email and your new password", // pragma: allowlist secret
    username: "Username",
    verificationCode: "Verification Code",
    newPassword: "New Password", // pragma: allowlist secret
    confirmNewPassword: "Confirm New Password", // pragma: allowlist secret
    sendResetCode: "Send Reset Code",
    sendingCode: "Sending Code...",
    resetPassword: "Reset Password", // pragma: allowlist secret
    resettingPassword: "Resetting Password...", // pragma: allowlist secret
    backToUsername: "Back to Username",
    backToSignIn: "Back to Sign In",
    errors: {
      passwordMismatch: "Passwords do not match", // pragma: allowlist secret
      passwordTooShort: "Password must be at least 8 characters long" // pragma: allowlist secret
    }
  },
  paymentCancel: {
    title: "Payment Cancelled",
    description: "Your payment was cancelled. No charges have been made to your account.",
    freeAccountInfo: "You can still create a free Basic account to access public catalogs and save personal albums.",
    tryAgain: "Try Again",
    continueWithFree: "Continue with Free Account"
  },
  paymentSuccess: {
    title: "Payment Successful!",
    description: "Thank you for subscribing to the {tier} plan. Your account has been created successfully. Please sign in with your email address to access your account.",
    passwordTip: "💡 Use the same password you entered during signup to sign in.",
    subscriptionId: "Subscription ID: {id}",
    verifying: "Verifying your payment...",
    continue: "Continue to Dashboard",
    returnToSignup: "Return to Sign Up"
  },
  objectDetails: {
    title: "Object Details",
    share: "Share",
    editObject: "Edit Object",
    shareObject: "Share Object",
    overview: "Overview",
    workType: "Getty Work Type:",
    technique: "Getty Technique:",
    materials: "Getty Materials:",
    dateCreated: "Date Created:",
    dimensions: "Dimensions:",
    condition: "Condition:",
    keywords: "Keywords:",
    provenance: "Provenance",
    provenanceDescription: "Provenance information will be displayed here when available.",
    exhibitions: "Exhibitions",
    exhibitionsDescription: "Exhibition history will be displayed here when available.",
    publications: "Publications",
    publicationsDescription: "Publication information will be displayed here when available.",
    awards: "Awards & Honors",
    awardsDescription: "Awards and honors associated with this object will be displayed here when available.",
    additionalInfo: "Additional Information",
    notes: "Notes:",
    location: "Location:",
    estimatedValue: "Estimated Value:",
    status: "Status:",
    created: "Created:",
    lastUpdated: "Last Updated:",
    noImageAvailable: "No Image Available"
  },
  publicCatalog: {
    title: "Public Catalog",
    searchObjects: "Search objects...",
    objects: "Objects",
    noObjectsYet: "No objects yet",
    noObjectsDescription: "Creators haven't shared any objects publicly yet. Check back later!",
    loadingCatalog: "Loading public catalog...",
    sharePublicCatalog: "Share Public Catalog",
    uploadObject: "Upload Object",
    infoBarMessage: "This content about your object is: Public"
  },
  subscriptionSelection: {
    title: "Choose Your Plan",
    subtitle: "Select the subscription tier that best fits your needs",
    continue: "Continue",
    tiers: {
      basic: {
        name: "Basic",
        price: "Free",
        description: "Join the Art Legacy Institute to explore artist catalogs, discover new work, and save what inspires you. Free access to public albums.",
        features: [
          "View public catalogs only",
          "Search and filter public content",
          "Save personal albums from public catalogs",
          "Comment/feedback on public content (if permitted)"
        ]
      },
      creator: {
        name: "Creator",
        price: "Paid Plan",
        description: "Built for artists to catalog their work, maintain control of their records, assign trusted delegates, and preserve their legacy for the future.",
        features: [
          "Single catalog",
          "Up to 3 delegates",
          "Up to 3 legacy managers",
          "Assigned ID (e.g. DA007)",
          "Create, edit, delete catalog entries",
          "Upload and manage images",
          "Share catalogs publicly/privately",
          "View personal analytics"
        ]
      },
      commercial: {
        name: "General Catalog",
        price: "Paid Plan",
        description: "Content creators and managers",
        features: [
          "Multiple catalogs",
          "Up to 10 delegates",
          "Up to 3 legacy managers",
          "Assigned ID (e.g. LG123)",
          "Create, edit, delete catalog entries",
          "Upload and manage images",
          "Bulk operations",
          "Share catalogs publicly/privately",
          "View personal analytics"
        ]
      }
    }
  },
  admin: {
    title: "Admin Panel",
    tabs: {
      getty: "Getty Data",
      search: "Search Index",
      processing: "Image Processing",
      userExport: "User Export",
      legalDocs: "Legal Docs",
      analytics: "Analytics",
      userLookup: "User Lookup",
      incidentBanner: "Incident Banner"
    },
    userLookup: {
      searchPlaceholder: "Search by email or ALI ID",
      search: "Search",
      searching: "Searching...",
      noResults: "No user found",
      userInfo: "User Information",
      name: "Name",
      email: "Email",
      aliId: "ALI ID",
      role: "Role",
      subscriptionPlan: "Subscription Plan",
      createdAt: "Created",
      objectCount: "Objects",
      objectCatalogsHeader: "Objects by Catalog",
      catalogAliId: "ALI",
      catalogCreator: "Creator",
      catalogRelationship: "Relationship",
      catalogCount: "Count",
      catalogPrimary: "Primary",
      catalogOrphan: "(unassigned)",
      collectionCount: "Albums",
      delegationCount: "Delegations",
      cognitoStatus: "Cognito Status",
      accountEnabled: "Account Enabled",
      mfaEnabled: "MFA Enabled",
      mfaMethods: "MFA Methods",
      healthChecks: "Health Checks",
      actions: "Actions",
      lockAccount: "Lock Account",
      unlockAccount: "Unlock Account",
      resetPassword: "Reset Password",  // pragma: allowlist secret
      lockConfirm: "Are you sure you want to lock this account? The user will be signed out and unable to log in.",
      unlockConfirm: "Are you sure you want to unlock this account? The user will be able to log in again.",
      resetConfirm: "Are you sure you want to send a password reset email to this user?",
      lockSuccess: "Account locked successfully",
      unlockSuccess: "Account unlocked successfully",
      resetSuccess: "Password reset email sent",
      error: "An error occurred",
      yes: "Yes",
      no: "No",
      notAssigned: "Not assigned",
      enabled: "Enabled",
      disabled: "Disabled",
      detectedType: "Detected type",
      nameCandidatesTitle: "Users matching",
      noNameMatches: "No users found matching",
      candidateNotAdministrable: "No identifier available",
      userType: "Type",
      artworkCount: "Artworks",
      deactivatedAt: "Deactivated",
      publicAccessPolicy: "Public Access",
      policyAuto: "Auto",
      policyBlocked: "Blocked",
      policyAllowed: "Allowed",
      policyConfirm: "Change this user's public-access policy to",
      policySuccess: "Public access policy updated",
      policyHelp: "Auto follows the subscription lifecycle per catalog. Blocked/Allowed pin public visibility regardless of it."
    },
    getty: {
      title: "Getty Vocabulary Data",
      workTypes: "Getty Work Types",
      materials: "Getty Materials",
      techniques: "Getty Techniques",
      signatures: "Signatures",
      count: "Count",
      lastUpdated: "Last Updated",
      fresh: "Fresh",
      stale: "Stale",
      notLoaded: "Not Loaded",
      rebuildAll: "Rebuild All",
      rebuild: "Rebuild",
      rebuilding: "Rebuilding...",
      rebuildSuccess: "Getty data rebuilt successfully",
      rebuildError: "Error rebuilding Getty data",
      confirmRebuild: "Are you sure you want to rebuild Getty data? This may take several minutes.",
      refreshDump: "Refresh Getty Data",
      refreshing: "Refreshing...",
      refreshSuccess: "Getty data refreshed from upstream dump",
      refreshUnchanged: "Getty data is already up to date — upstream dump unchanged",
      refreshError: "Error refreshing Getty data from upstream",
      confirmRefresh: "Fetch the latest Getty AAT dump from upstream and rebuild all four hierarchy caches? This may take 2-5 minutes.",
      conceptId: "Concept ID",
      editConceptId: "Edit Concept ID",
      preview: "Preview",
      previewing: "Previewing...",
      applyAndRebuild: "Apply & Rebuild",
      applying: "Applying...",
      applySuccess: "Configuration updated and rebuild started",
      applyError: "Error updating configuration",
      previewError: "Error previewing concept ID",
      invalidConceptId: "Concept ID must be a numeric Getty AAT identifier (6-12 digits)",
      previewResults: "Preview Results",
      conceptLabel: "Concept Label",
      topLevelNodes: "Top-level Categories",
      totalResults: "Total Terms",
      currentConfig: "Current",
      newConceptId: "New Concept ID",
      sparqlTemplate: "SPARQL Query Template",
      sparqlHelperText: "Use {concept_id} as placeholder for the root concept. Query must SELECT ?concept and ?label.",
      resetToDefault: "Reset to Default",
      customQuery: "Custom Query"
    },
    search: {
      maintenanceTitle: "Search Maintenance",
      title: "Search Index Management",
      description: "Reindex all catalog items to Postgres FTS. Rebuilds the full-text search projection.",
      reindexAll: "Reindex All",
      reindexing: "Reindexing...",
      reindexSuccess: "Search index rebuilt successfully",
      reindexError: "Error rebuilding search index",
      confirmReindex: "Are you sure you want to reindex all catalog items? This may take several minutes depending on the number of items.",
      itemsProcessed: "Items Processed",
      refreshed: "Refreshed",
      missing: "Missing",
      errors: "Errors"
    },
    processing: {
      title: "Image Processing",
      description: "Monitor and manage the image processing pipeline. Redrive failed messages from the Dead Letter Queue.",
      dlqTitle: "Dead Letter Queue",
      mainQueueTitle: "Main Queue",
      messagesAvailable: "Messages Available",
      messagesInFlight: "In Flight",
      messagesDelayed: "Delayed",
      stuckItemsTitle: "Stuck Items",
      stuckItemsDescription: "Catalog items stuck in PROCESSING status for more than 30 minutes.",
      noStuckItems: "No stuck items found.",
      redrive: "Redrive Messages",
      redriving: "Redriving...",
      redriveSuccess: "Messages redriven successfully",
      redriveError: "Error redriving messages",
      confirmRedrive: "Are you sure you want to redrive DLQ messages back to the main queue for reprocessing?",
      refresh: "Refresh",
      messagesRedriven: "Messages Redriven",
      queueNotConfigured: "Queue URLs not configured",
      failedItemsTitle: "Failed Items",
      failedItemsDescription: "Catalog items that failed during image processing.",
      noFailedItems: "No failed items found.",
      retryItem: "Retry",
      retryAll: "Retry All",
      retrying: "Retrying...",
      retrySuccess: "Items queued for reprocessing",
      retryError: "Error retrying items",
      confirmRetry: "Are you sure you want to retry these items? They will be requeued for image processing."
    },
    userExport: {
      title: "User Data Export",
      description: "Export consenting user contact data as CSV for CRM integration (HubSpot, Salesforce). Only users who have opted into marketing data sharing are included.",
      fields: {
        title: "Select Fields",
        selectAll: "Select All",
        selectNone: "Select None",
        categories: {
          contact: "Contact",
          account: "Account",
          business: "Business",
          meta: "Meta"
        }
      },
      filters: {
        title: "Filters",
        roles: "Roles",
        subscriptionPlans: "Subscription Plans",
        createdAfter: "Created After",
        createdAfterHelp: "Leave blank for no start date",
        createdBefore: "Created Before",
        createdBeforeHelp: "Leave blank for no end date"
      },
      preview: "Preview",
      previewResult: "matching consented users",
      export: "Export CSV",
      exporting: "Exporting...",
      noFields: "Select at least one field to export",
      confirmTitle: "Confirm Export",
      confirmMessage: "Download CSV with the selected fields?",
      cancel: "Cancel",
      confirm: "Download",
      exportError: "Failed to export user data"
    },
    legalDocs: {
      termsTitle: "Terms of Service",
      privacyTitle: "Privacy Policy",
      uploadButton: "Upload New Version",
      uploadButtonLoading: "Uploading...",
      publishButton: "Publish Version",
      publishButtonLoading: "Publishing...",
      discardUpload: "Discard Upload",
      closePreview: "Close Preview",
      emptyStateHeading: "No versions uploaded yet",
      emptyStateBody: "Upload a PDF to create the first version. All users will need to accept it.",
      successBanner: "Version {version} published. Users will be prompted to re-accept on next login.",
      errorUpload: "Upload failed. Please try again.",
      errorFileType: "Only PDF files can be uploaded.",
      errorFileSize: "File must be 10 MB or smaller.",
      errorLoadVersions: "Failed to load version history. Refresh to try again.",
      dialogTitleNewUpload: "Preview: {docType} \u2014 Version {version}",
      dialogTitleExisting: "{docType} \u2014 Version {version}",
      fileMetadataFormat: "{fileName} \u00b7 {size} \u00b7 {pageCount} pages",
      publishWarning: "This will publish version {version} as the current {docType}. All users will need to re-accept.",
      versionHistoryHeading: "Version History",
      tableVersion: "Version",
      tableEffectiveDate: "Effective Date",
      tableFileName: "File Name",
      tableFileSize: "File Size",
      tableUploadedBy: "Uploaded By",
      tableUploadedAt: "Uploaded At",
      currentChip: "Current",
    },
    analytics: {
      loading: "Loading analytics dashboard...",
      error: "Failed to load analytics dashboard.",
      disabled: "Analytics dashboards are not available in this environment.",
      openInQuickSight: "Open in QuickSight",
      refresh: "Refresh",
      dashboards: {
        "platform-overview": "Platform Overview",
        "user-activity": "User Activity",
        "content-analytics": "Content Analytics",
        "business-metrics": "Business Metrics",
      },
    },
    incidentBanner: {
      title: "Incident Banner",
      description: "Display a banner message to all users when there are known issues or planned maintenance.",
      enabled: "Banner Enabled",
      enabledDescription: "When enabled, the banner is visible to all users including unauthenticated visitors.",
      severity: "Severity",
      severityInfo: "Info",
      severityWarning: "Warning",
      severityError: "Error",
      message: "Banner Message",
      messagePlaceholder: "Brief message shown to all users (max 500 characters)",
      details: "Expandable Details",
      detailsPlaceholder: "Additional details shown when users click 'More details' (optional)",
      preview: "Preview",
      save: "Save Banner Settings",
      saving: "Saving...",
      saveSuccess: "Banner settings saved successfully",
      saveError: "Failed to save banner settings",
      lastUpdated: "Last updated",
      updatedBy: "Updated by",
    }
  },
  versionCheck: {
    newVersionMessage: "A new version of the application is available.",
    updateButton: "Update Now",
    dismissLabel: "Dismiss update notification",
  },
  displayNameNotice: {
    title: "Set up your display name",
    message: "Your display name is how you appear as a creator on your catalog objects. Visit your",
    linkText: "Settings",
    messageSuffix: "page to choose a display name."
  },
  legal: {
    termsTitle: "Terms of Service",
    privacyTitle: "Privacy Policy",
    version: "v{version}",
    effective: "Effective {date}",
    downloadPdf: "Download PDF",
  },
  invitation: {
    title: "Delegation Invitation",
    loading: "Loading invitation...",
    errors: {
      noToken: "No invitation token provided.",
      fetchFailed: "Failed to load invitation details.",
      expired: "This invitation has expired.",
      accepted: "This invitation has already been accepted.",
      revoked: "This invitation has been revoked.",
      acceptFailed: "Failed to accept invitation.",
      emailMismatch: "Your account email does not match this invitation.",
      alreadyProcessed: "This invitation has already been processed.",
    },
    card: {
      from: "From",
      collection: "Album",
      accessLevel: "Access Level",
      expires: "Expires",
      message: "Message",
    },
    permissions: {
      view: "View Only",
      full: "Full Access",
    },
    actions: {
      accept: "Accept Invitation",
      accepting: "Accepting...",
      signIn: "Sign In to Accept",
      createAccount: "Create Delegate Account",
      signInLink: "Already have an account? Click here to accept the collaboration request",
      signOutTryAnother: "Sign Out & Try Another Account",
      goHome: "Go to Home",
    },
    success: "Invitation accepted successfully!",
    signupBanner: "You've been invited to collaborate on {collectionName} by {delegatorName}",
  },
} as const;
